// Margin.jsx
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaToggleOn, FaToggleOff, FaTimes } from "react-icons/fa";
import Swal from 'sweetalert2';
import { useLocation } from "react-router-dom";

// All categories list
const ALL_CATEGORIES = [
    'Broadband Postpaid', 'Cable TV', 'Clubs and Associations', 'Credit Card', 'DTH',
    'Education Fees', 'Electricity', 'Fastag', 'Gas', 'Health Insurance', 'Hospital',
    'Hospital and Pathology', 'Housing Society', 'Insurance', 'Landline Postpaid',
    'Life Insurance', 'Loan Repayment', 'LPG Gas', 'Mobile Postpaid', 'Municipal Services',
    'Municipal Taxes', 'Postpaid', 'Prepaid', 'Recurring Deposit', 'Redeem Code',
    'Subscription', 'Water', 'Donation', 'Prepaid Meter'
];

function Margin() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [margins, setMargins] = useState([]);
    const [filteredMargins, setFilteredMargins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Modal states
    const [marginModalOpen, setMarginModalOpen] = useState(false);
    const [selectedMargin, setSelectedMargin] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Plan states
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);

    // Provider states
    const [providers, setProviders] = useState([]);
    const [selectedProviderName, setSelectedProviderName] = useState('');
    const [selectedProviderId, setSelectedProviderId] = useState('');
    const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
    const [providerModalList, setProviderModalList] = useState([]);
    const [providerModalLoading, setProviderModalLoading] = useState(false);
    const [providerModalSearch, setProviderModalSearch] = useState('');

    // Operator states
    const [operators, setOperators] = useState([]);
    const [selectedOperatorName, setSelectedOperatorName] = useState('');
    const [selectedOperatorId, setSelectedOperatorId] = useState('');
    const [isOperatorModalOpen, setIsOperatorModalOpen] = useState(false);
    const [operatorModalList, setOperatorModalList] = useState([]);
    const [operatorModalLoading, setOperatorModalLoading] = useState(false);
    const [operatorModalSearch, setOperatorModalSearch] = useState('');

    const location = useLocation();
    const plan_id = location.state?.plan_id || '';
    const operator_id = location.state?.operator_id || '';
    const provider_id = location.state?.provider_id || '';

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states - ADDED providerId
    const [filters, setFilters] = useState({
        searchValue: '',
        status: '',
        planId: plan_id || '',
        operatorId: operator_id || '',
        providerId: provider_id || '', // NEW: Provider filter
        dateFrom: '',
        dateTo: ''
    });

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Commission type options
    const comTypeOptions = [
        { id: '1', name: 'Percentage' },
        { id: '2', name: 'Fixed' },
        { id: '3', name: 'Amount' }
    ];

    // State options
    const stateOptions = [
        { id: 'All', name: 'All' },
        { id: 'Assam', name: 'Assam' }
    ];

    // Form state for add/edit
    const [formData, setFormData] = useState({
        plan_id: '',
        operator_id: '',
        api_end_operator_id: '',
        api_com_type: '1',
        api_com: '',
        user_com_type: '1',
        user_com: '',
        dist_com_type: '1',
        dist_com: '',
        amount: '',
        amounts: '',
        min_amount: '',
        max_amount: '',
        block_amount: '',
        isRoffer: '0',
        state: 'All',
        provider_id: '',
        status: '1',
        priority: ''
    });

    // Fetch Plans
    const fetchPlans = async () => {
        setPlansLoading(true);
        try {
            const result = await apiCall(
                "margin-plan",
                {},
                true
            );

            if (result.status === true) {
                setPlans(result.data || []);
            } else {
                toast.error(result.message || "Failed to fetch plans");
                setPlans([]);
            }
        } catch (error) {
            setPlans([]);
        } finally {
            setPlansLoading(false);
        }
    };

    // Fetch Providers
    const fetchProviders = async () => {
        try {
            const result = await apiCall(
                "provider",
                {
                    category: "1"
                },
                true
            );

            if (result.status === true) {
                const providerList = result.provider.map(provider => ({
                    id: provider.id,
                    name: provider.name
                }));
                setProviders(providerList);
                setProviderModalList(providerList);
                return providerList;
            } else {
                toast.error(result.message);
                return [];
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            return [];
        }
    };

    // Fetch Operators with typing search
    const fetchOperators = async (typing = '') => {
        try {
            const result = await apiCall(
                "operators",
                {
                    typing: typing,
                    category: ALL_CATEGORIES,
                    sub_category: 'Non BBPS'
                },
                true
            );

            if (result.status === true) {
                const operatorList = result.operators.map(operator => ({
                    id: operator.operator_id,
                    name: operator.biller_name,
                    logo: operator.logo
                }));
                setOperators(operatorList);
                return operatorList;
            } else {
                return [];
            }
        } catch (error) {
            return [];
        }
    };

    // Open Provider Modal
    const openProviderModal = async () => {
        setIsProviderModalOpen(true);
        setProviderModalSearch('');
        setProviderModalLoading(true);
        const result = await fetchProviders();
        setProviderModalList(result);
        setProviderModalLoading(false);
    };

    // Close Provider Modal
    const closeProviderModal = () => {
        setIsProviderModalOpen(false);
        setProviderModalSearch('');
    };

    // Handle Provider Search
    const handleProviderModalSearch = (value) => {
        setProviderModalSearch(value);
        const filtered = providers.filter(p =>
            p.name.toLowerCase().includes(value.toLowerCase())
        );
        setProviderModalList(filtered);
    };

    // Select Provider
    const selectProvider = (provider) => {
        handleFormChange('provider_id', provider.id);
        setSelectedProviderName(provider.name);
        setSelectedProviderId(provider.id);
        closeProviderModal();
    };

    // Clear Provider
    const clearProvider = () => {
        handleFormChange('provider_id', '');
        setSelectedProviderName('');
        setSelectedProviderId('');
    };

    // Open Operator Modal
    const openOperatorModal = async () => {
        setIsOperatorModalOpen(true);
        setOperatorModalSearch('');
        setOperatorModalLoading(true);
        const result = await fetchOperators('');
        setOperatorModalList(result);
        setOperatorModalLoading(false);
    };

    // Close Operator Modal
    const closeOperatorModal = () => {
        setIsOperatorModalOpen(false);
        setOperatorModalSearch('');
    };

    // Handle Operator Search
    const handleOperatorModalSearch = async (value) => {
        setOperatorModalSearch(value);
        setOperatorModalLoading(true);
        const result = await fetchOperators(value);
        setOperatorModalList(result);
        setOperatorModalLoading(false);
    };

    // Select Operator
    const selectOperator = (operator) => {
        handleFormChange('operator_id', operator.id);
        setSelectedOperatorName(operator.name);
        setSelectedOperatorId(operator.id);
        closeOperatorModal();
    };

    // Clear Operator
    const clearOperator = () => {
        handleFormChange('operator_id', '');
        setSelectedOperatorName('');
        setSelectedOperatorId('');
    };

    // Fetch Margins with pagination - UPDATED to include providerId
    const fetchMargins = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                status: filters.status,
                planId: filters.planId,
                operatorId: filters.operatorId,
                providerId: filters.providerId, // NEW: Include provider filter
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "margin",
                params,
                true
            );

            if (result.status === true) {
                setMargins(result.data || []);
                setFilteredMargins(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch margins");
                setMargins([]);
                setFilteredMargins([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setMargins([]);
            setFilteredMargins([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Add Margin
    const handleAddMargin = () => {
        setIsEditMode(false);
        setFormData({
            plan_id: '',
            operator_id: '',
            api_end_operator_id: '',
            api_com_type: '1',
            api_com: '',
            user_com_type: '1',
            user_com: '',
            dist_com_type: '1',
            dist_com: '',
            amount: '',
            amounts: '',
            min_amount: '',
            max_amount: '',
            block_amount: '',
            isRoffer: '0',
            state: 'All',
            provider_id: '',
            status: '1',
            priority: ''
        });
        setSelectedOperatorName('');
        setSelectedOperatorId('');
        setSelectedProviderName('');
        setSelectedProviderId('');
        setSelectedMargin(null);
        setMarginModalOpen(true);
    };

    // Handle Edit Margin
    const handleEditMargin = (margin) => {
        setIsEditMode(true);
        setSelectedMargin(margin);
        setFormData({
            plan_id: margin.plan_id || '',
            operator_id: margin.operator_id || '',
            api_end_operator_id: margin.api_end_operator_id || '',
            api_com_type: margin.api_com_type_id || '1',
            api_com: margin.api_com || '',
            user_com_type: margin.user_com_type_id || '1',
            user_com: margin.user_com || '',
            dist_com_type: margin.dist_com_type_id || '1',
            dist_com: margin.dist_com || '',
            amount: margin.amount || '',
            amounts: margin.amounts || '',
            min_amount: margin.min_amount || '',
            max_amount: margin.max_amount || '',
            block_amount: margin.block_amount || '',
            isRoffer: margin.isRoffer_id || '0',
            state: margin.state || 'All',
            provider_id: margin.provider_id || '',
            status: margin.status_id || '1',
            priority: margin.priority || ''
        });
        setSelectedOperatorName(margin.operator_name || '');
        setSelectedOperatorId(margin.operator_id || '');
        setSelectedProviderName(margin.provider_name || '');
        setSelectedProviderId(margin.provider_id || '');
        setMarginModalOpen(true);
    };

    // Handle Delete Margin
    const handleDeleteMargin = (margin) => {
        Swal.fire({
            title: 'Delete Margin?',
            text: `Are you sure you want to delete this margin?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "delete-margin",
                        {
                            margin_id: margin.margin_id
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success("Margin deleted successfully!");
                        fetchMargins(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to delete margin");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Status Toggle
    const handleStatusToggle = (margin) => {
        const newStatus = margin.status === 'Active' ? '0' : '1';
        const statusName = margin.status === 'Active' ? 'Inactive' : 'Active';

        Swal.fire({
            title: `Toggle Status?`,
            text: `Are you sure you want to change this margin to ${statusName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Toggle!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "toggle-margin-status",
                        {
                            margin_id: margin.margin_id,
                            status: newStatus
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success(`Margin ${statusName} successfully!`);
                        fetchMargins(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to toggle status");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Save Margin (Add/Update)
    const handleSaveMargin = async () => {
        if (!formData.plan_id) {
            toast.error("Please select a plan");
            return;
        }

        if (!formData.operator_id) {
            toast.error("Please select an operator");
            return;
        }

        try {
            const endpoint = isEditMode ? "update-margin" : "add-margin";
            const params = isEditMode
                ? { margin_id: selectedMargin.margin_id, ...formData }
                : formData;

            const response = await apiCall(endpoint, params, true);

            if (response.status === true) {
                toast.success(isEditMode ? "Margin updated successfully!" : "Margin added successfully!");
                setMarginModalOpen(false);
                fetchMargins(pagination.currentPage);
            } else {
                toast.error(response.message || "Failed to save margin");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchMargins(1);
        fetchPlans();
        fetchProviders();
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchMargins(1);
    };

    // Reset filters - UPDATED to include providerId
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            status: '',
            planId: '',
            operatorId: '',
            providerId: '', // NEW: Reset provider filter
            dateFrom: '',
            dateTo: ''
        });
        fetchMargins(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchMargins(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Handle form change
    const handleFormChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Margins</h1>
                            <p className="text-gray-600 text-sm">View and manage all margin configurations</p>
                        </div>
                        <button
                            onClick={handleAddMargin}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2"
                        >
                            <FaPlus />
                            Add Margin
                        </button>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder="Search by operator..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>{option.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                                    <select
                                        value={filters.planId}
                                        onChange={(e) => handleFilterChange('planId', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">All Plans</option>
                                        {plans.map(plan => (
                                            <option key={plan.plan_id} value={plan.plan_id}>
                                                {plan.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Operator ID</label>
                                    <input
                                        type="text"
                                        value={filters.operatorId}
                                        onChange={(e) => handleFilterChange('operatorId', e.target.value)}
                                        placeholder="Filter by operator ID..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>
                                {/* NEW: Provider Filter Dropdown */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                                    <select
                                        value={filters.providerId}
                                        onChange={(e) => handleFilterChange('providerId', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">All Providers</option>
                                        {providers.map(provider => (
                                            <option key={provider.id} value={provider.id}>
                                                {provider.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex gap-2 items-end">
                                    <button onClick={applyFilters} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">Apply</button>
                                    <button onClick={resetFilters} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Reset</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredMargins.length}</span> margins
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Margins Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amounts</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Block Amount</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">rOffer</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredMargins.length > 0 ? (
                                        filteredMargins.map((margin) => (
                                            <tr key={margin.margin_id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">#{margin.margin_id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{margin.plan_name}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.operator_name}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.provider_name}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">₹{parseFloat(margin.amount || 0).toFixed(2)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{margin.amounts}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">₹{margin.min_amount}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">₹{parseFloat(margin.max_amount || 0).toFixed(2)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm ftext-gray-900">₹{parseFloat(margin.block_amount || 0).toFixed(2)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.user_com} ({margin.user_com_type})</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.dist_com} ({margin.dist_com_type})</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.api_com} ({margin.api_com_type})</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.isRoffer}</td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{margin.state}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(margin.status)}`}>
                                                            {margin.status}
                                                        </span>
                                                        <button onClick={() => handleStatusToggle(margin)} className="text-gray-500 hover:text-blue-600 transition-colors">
                                                            {margin.status === 'Active' ? <FaToggleOn className="text-2xl text-green-600" /> : <FaToggleOff className="text-2xl text-gray-400" />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => handleEditMargin(margin)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><FaEdit className="text-sm" /></button>
                                                        <button onClick={() => handleDeleteMargin(margin)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><FaTrash className="text-sm" /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="16" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No margins found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</div>
                            <div className="flex gap-2">
                                <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>Previous</button>
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) pageNum = i + 1;
                                        else if (pagination.currentPage <= 3) pageNum = i + 1;
                                        else if (pagination.currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                                        else pageNum = pagination.currentPage - 2 + i;
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{pageNum}</button>
                                        );
                                    })}
                                </div>
                                <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Margin Modal */}
            {marginModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setMarginModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            {/* Header */}
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Edit Margin' : 'Add New Margin'}</h3>
                                    <p className="text-sm text-gray-500">{isEditMode ? `Editing margin #${selectedMargin?.margin_id}` : 'Create a new margin configuration'}</p>
                                </div>
                                <button onClick={() => setMarginModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Plan - Dropdown */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Plan <span className="text-red-500">*</span></label>
                                        <select
                                            value={formData.plan_id}
                                            onChange={(e) => handleFormChange('plan_id', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="">Select Plan</option>
                                            {plansLoading ? (
                                                <option value="" disabled>Loading plans...</option>
                                            ) : (
                                                plans.map(plan => (
                                                    <option key={plan.plan_id} value={plan.plan_id}>
                                                        {plan.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>

                                    {/* Operator - Searchable */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Operator <span className="text-red-500">*</span></label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={selectedOperatorName}
                                                readOnly
                                                placeholder="Click to search operator..."
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer text-sm"
                                                onClick={openOperatorModal}
                                            />
                                            {formData.operator_id && (
                                                <button onClick={clearOperator} className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"><FaTimes /></button>
                                            )}
                                            <button onClick={openOperatorModal} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"><FaSearch /></button>
                                        </div>
                                        {selectedOperatorId && (
                                            <div className="mt-1 text-xs text-gray-500">
                                                Operator ID: {selectedOperatorId}
                                            </div>
                                        )}
                                    </div>

                                    {/* API End Operator ID */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">API End Operator ID</label>
                                        <input type="text" value={formData.api_end_operator_id} onChange={(e) => handleFormChange('api_end_operator_id', e.target.value)} placeholder="Enter API end operator ID" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>

                                    {/* Provider - Searchable */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={selectedProviderName}
                                                readOnly
                                                placeholder="Click to search provider..."
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer text-sm"
                                                onClick={openProviderModal}
                                            />
                                            {formData.provider_id && (
                                                <button onClick={clearProvider} className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"><FaTimes /></button>
                                            )}
                                            <button onClick={openProviderModal} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"><FaSearch /></button>
                                        </div>
                                        {selectedProviderId && (
                                            <div className="mt-1 text-xs text-gray-500">
                                                Provider ID: {selectedProviderId}
                                            </div>
                                        )}
                                        {selectedProviderName && (
                                            <div className="mt-1 text-xs text-gray-500">
                                                Provider Name: {selectedProviderName}
                                            </div>
                                        )}
                                    </div>

                                    {/* API Commission */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">API Commission</label>
                                        <div className="flex gap-2">
                                            <input type="number" step="0.001" value={formData.api_com} onChange={(e) => handleFormChange('api_com', e.target.value)} placeholder="Commission" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                            <select value={formData.api_com_type} onChange={(e) => handleFormChange('api_com_type', e.target.value)} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                                                {comTypeOptions.map(option => (<option key={option.id} value={option.id}>{option.name}</option>))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* User Commission */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">User Commission</label>
                                        <div className="flex gap-2">
                                            <input type="number" step="0.001" value={formData.user_com} onChange={(e) => handleFormChange('user_com', e.target.value)} placeholder="Commission" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                            <select value={formData.user_com_type} onChange={(e) => handleFormChange('user_com_type', e.target.value)} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                                                {comTypeOptions.map(option => (<option key={option.id} value={option.id}>{option.name}</option>))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Distributor Commission */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Distributor Commission</label>
                                        <div className="flex gap-2">
                                            <input type="number" step="0.001" value={formData.dist_com} onChange={(e) => handleFormChange('dist_com', e.target.value)} placeholder="Commission" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                            <select value={formData.dist_com_type} onChange={(e) => handleFormChange('dist_com_type', e.target.value)} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                                                {comTypeOptions.map(option => (<option key={option.id} value={option.id}>{option.name}</option>))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Amount */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                        <input type="number" step="0.001" value={formData.amount} onChange={(e) => handleFormChange('amount', e.target.value)} placeholder="Enter amount" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>

                                    {/* Amounts */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amounts</label>
                                        <input type="text" value={formData.amounts} onChange={(e) => handleFormChange('amounts', e.target.value)} placeholder="Enter amounts (comma separated)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>

                                    {/* Min Amount */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
                                        <input type="number" step="0.001" value={formData.min_amount} onChange={(e) => handleFormChange('min_amount', e.target.value)} placeholder="Enter min amount" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>

                                    {/* Max Amount */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount</label>
                                        <input type="number" step="0.001" value={formData.max_amount} onChange={(e) => handleFormChange('max_amount', e.target.value)} placeholder="Enter max amount" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>

                                    {/* Block Amount */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Block Amount</label>
                                        <input type="text" value={formData.block_amount} onChange={(e) => handleFormChange('block_amount', e.target.value)} placeholder="Enter block amount" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>

                                    {/* Is Roffer */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Is Roffer</label>
                                        <select value={formData.isRoffer} onChange={(e) => handleFormChange('isRoffer', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                                            <option value="0">No</option><option value="1">Yes</option>
                                        </select>
                                    </div>

                                    {/* State */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                        <select value={formData.state} onChange={(e) => handleFormChange('state', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                                            {stateOptions.map(option => (<option key={option.id} value={option.id}>{option.name}</option>))}
                                        </select>
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select value={formData.status} onChange={(e) => handleFormChange('status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                                            <option value="1">Active</option><option value="0">Inactive</option>
                                        </select>
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                        <input type="number" value={formData.priority} onChange={(e) => handleFormChange('priority', e.target.value)} placeholder="Enter priority" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
                                <button onClick={() => setMarginModalOpen(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Cancel</button>
                                <button onClick={handleSaveMargin} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">{isEditMode ? 'Update' : 'Add'} Margin</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Provider Search Modal */}
            {isProviderModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={closeProviderModal}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden">
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-gray-900">Search Provider</h3>
                                <button onClick={closeProviderModal} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input type="text" value={providerModalSearch} onChange={(e) => handleProviderModalSearch(e.target.value)} placeholder="Type to search providers..." className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" autoFocus />
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 min-h-[200px] max-h-[400px]">
                                {providerModalLoading ? (
                                    <div className="flex flex-col items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div><p className="mt-4 text-gray-600 text-sm">Loading providers...</p></div>
                                ) : providerModalList.length > 0 ? (
                                    <div className="space-y-2">
                                        {providerModalList.map((provider) => (
                                            <div
                                                key={provider.id}
                                                onClick={() => selectProvider(provider)}
                                                className={`px-4 py-3 rounded-lg cursor-pointer flex items-center gap-3 transition-colors hover:bg-gray-50 border-2 border-transparent ${formData.provider_id === provider.id ? 'bg-blue-50 border-blue-500' : ''
                                                    }`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{provider.name}</p>
                                                    <p className="text-xs text-gray-500">ID: {provider.id}</p>
                                                </div>
                                                {formData.provider_id === provider.id && (
                                                    <span className="text-blue-600 flex-shrink-0">
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                        <p className="text-gray-500">No providers found</p>
                                        <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                                    </div>
                                )}
                            </div>
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center">
                                <span className="text-xs text-gray-500">{providerModalList.length > 0 && `${providerModalList.length} providers found`}</span>
                                <button onClick={closeProviderModal} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Operator Search Modal */}
            {isOperatorModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={closeOperatorModal}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden">
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-gray-900">Search Operator</h3>
                                <button onClick={closeOperatorModal} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input type="text" value={operatorModalSearch} onChange={(e) => handleOperatorModalSearch(e.target.value)} placeholder="Type to search operators..." className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" autoFocus />
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 min-h-[200px] max-h-[400px]">
                                {operatorModalLoading ? (
                                    <div className="flex flex-col items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div><p className="mt-4 text-gray-600 text-sm">Loading operators...</p></div>
                                ) : operatorModalList.length > 0 ? (
                                    <div className="space-y-2">
                                        {operatorModalList.map((operator) => (
                                            <div
                                                key={operator.id}
                                                onClick={() => selectOperator(operator)}
                                                className={`px-4 py-3 rounded-lg cursor-pointer flex items-center gap-3 transition-colors hover:bg-gray-50 border-2 border-transparent ${formData.operator_id === operator.id ? 'bg-blue-50 border-blue-500' : ''
                                                    }`}
                                            >
                                                {operator.logo && <img src={operator.logo} alt={operator.name} className="w-8 h-8 rounded-full object-contain flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{operator.name}</p>
                                                    <p className="text-xs text-gray-500">ID: {operator.id}</p>
                                                </div>
                                                {formData.operator_id === operator.id && (
                                                    <span className="text-blue-600 flex-shrink-0">
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                        <p className="text-gray-500">No operators found</p>
                                        <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                                    </div>
                                )}
                            </div>
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center">
                                <span className="text-xs text-gray-500">{operatorModalList.length > 0 && `${operatorModalList.length} operators found`}</span>
                                <button onClick={closeOperatorModal} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default Margin;